// src/shared_new/modules/ChatInterface.jsx
import React, { useEffect, useRef } from "react";
import { Box, IconButton, CircularProgress, useTheme, alpha, Tooltip, Switch, FormControlLabel } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import PerlLabelMedium from "../components/PerlLabelMedium";
import { useAuth } from "../../context/AuthContext";

/**
 * ChatInterface Component
 *
 * Interactive chat-style form with step-by-step questions
 * Displays progress indicator and navigation controls
 * Accepts React components (questions) as children for maximum flexibility
 *
 * @param {Array} questions - Array of React components (question components)
 * @param {number} currentStep - Current step index
 * @param {function} onStepChange - Callback when step changes
 * @param {function} onComplete - Callback when all steps completed
 * @param {string} brandColor - Brand color for styling
 * @param {function} isNextDisabled - Optional function to determine if next is disabled
 * @param {function} onReset - Optional callback for reset button
 * @param {boolean} autoAdvanceOnSelect - Auto-advance when selection is made (default: false)
 * @param {boolean} autoAdvanceOnBlur - Auto-advance when textbox loses focus (default: false)
 * @param {boolean} demoMode - Optional demo mode state
 * @param {function} onDemoToggle - Optional callback for demo toggle
 */
export default function ChatInterface({
  questions = [],
  currentStep = 0,
  onStepChange,
  onComplete,
  brandColor = "#ff6034",
  isNextDisabled,
  onReset,
  autoAdvanceOnSelect = false,
  autoAdvanceOnBlur = false,
  demoMode = false,
  onDemoToggle,
}) {
  const theme = useTheme();
  const { user } = useAuth();
  const perlColors = theme.custom?.perlColors || {};
  const completionTriggeredRef = useRef(false);

  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "").trim().toLowerCase();
    }
    return String(user?.role || "").trim().toLowerCase();
  })();

  const isAdminRole = normalizedRole === "admin";
  const isSuperAdminRole = normalizedRole === "super admin";
  const canSeeDemoToggle = isAdminRole || isSuperAdminRole;

  const totalSteps = questions.length;
  const currentQuestion = questions[currentStep];
  const validationDisabled = isNextDisabled ? isNextDisabled(currentStep) : false;
  const isLastStep = totalSteps > 0 && currentStep >= totalSteps - 1;
  const isComplete = isLastStep && !validationDisabled;

  // Auto-trigger completion and lock the Next button when the last question is answered
  useEffect(() => {
    if (isComplete && !completionTriggeredRef.current) {
      completionTriggeredRef.current = true;
      onComplete?.();
    } else if (!isComplete) {
      completionTriggeredRef.current = false;
    }
  }, [isComplete, onComplete]);

  // const handleNext = () => {
  //   if (currentStep < totalSteps - 1) {
  //     onStepChange?.(currentStep + 1);
  //   } else {
  //     onComplete?.();
  //   }
  // };
  const handleNext = React.useCallback(() => {
    if (currentStep < totalSteps - 1) {
      onStepChange?.(currentStep + 1);
    }
  }, [currentStep, totalSteps, onComplete, onStepChange]);

  const handleBack = () => {
    if (currentStep > 0) {
      onStepChange?.(currentStep - 1);
    }
  };

  // const isLastStep = currentStep === totalSteps - 1;
  const nextDisabled = isNextDisabled ? isNextDisabled(currentStep) : false;
  const nextButtonDisabled = nextDisabled || isLastStep;

  // Auto-advance handler that can be triggered by child components
  const handleAutoAdvance = React.useCallback(() => {
    // For auto-advance, skip validation check since user actively made a selection
    // Add small delay to ensure state updates are processed
    setTimeout(() => {
      if (currentStep < totalSteps - 1) {
        onStepChange?.(currentStep + 1);
      }
    }, 150); // Increased delay to allow React state updates to complete
  }, [currentStep, totalSteps, onStepChange]);

  // Enter key handler
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Enter" && !nextButtonDisabled) {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleNext, nextButtonDisabled]);

  // Clone question component and inject auto-advance handlers
  const enhancedQuestion = React.useMemo(() => {
    if (!currentQuestion) return null;

    return React.cloneElement(currentQuestion, {
      onAutoAdvance: handleAutoAdvance,
      autoAdvanceOnSelect: autoAdvanceOnSelect,
      autoAdvanceOnBlur: autoAdvanceOnBlur,
    });
  }, [currentQuestion, handleAutoAdvance, autoAdvanceOnSelect, autoAdvanceOnBlur]);

  return (
    <Box
      sx={{
        // width: "550px",
        height: "100%",
        border: "1px solid",
        borderColor: perlColors.border || "rgba(255, 255, 255, 0.12)",
        borderRadius: 2,
        p: 3,
        bgcolor: perlColors.surface || "rgba(255, 255, 255, 0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Chat Header with Reset Button and Progress Indicator */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pb: 1,
          mb: 2,
          borderBottom: "1px solid",
          borderColor: perlColors.border || "rgba(255, 255, 255, 0.12)",
        }}
      >
        {/* Reset Button and Demo Toggle - Left Side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {onReset && (
            <Tooltip title="Reset Form">
              <IconButton
                onClick={onReset}
                sx={{
                  color: perlColors.textSecondary || "#9097A1",
                  "&:hover": {
                    color: brandColor,
                    bgcolor: alpha(brandColor, 0.1),
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {onDemoToggle && canSeeDemoToggle && (
            <FormControlLabel
              control={
                <Switch
                  checked={demoMode}
                  onChange={(e) => onDemoToggle(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: brandColor,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: brandColor,
                    },
                  }}
                />
              }
              label="Demo"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: perlColors.textSecondary || "#9097A1",
                  fontFamily: theme.typography.fontFamily,
                },
              }}
            />
          )}
        </Box>

        {/* Progress Indicator - Right Side */}
        <Box sx={{ position: "relative", display: "inline-flex", ml: "auto" }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={32}
            thickness={5}
            sx={{ color: "rgba(0, 0, 0, 0.1)", position: "absolute" }}
          />
          <CircularProgress
            variant="determinate"
            value={((currentStep + 1) / totalSteps) * 100}
            size={32}
            thickness={5}
            sx={{
              color: brandColor,
              "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PerlLabelMedium
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: brandColor,
              }}
            >
              {currentStep + 1}/{totalSteps}
            </PerlLabelMedium>
          </Box>
        </Box>
      </Box>

      {/* Content Area - Scrollable question component */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          pr: 1,
          pt: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: perlColors.border,
            borderRadius: "3px",
          },
        }}
      >
        {/* Render the enhanced question component with auto-advance handlers */}
        {enhancedQuestion}
      </Box>

      {/* Navigation Buttons at Bottom */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: perlColors.border || "rgba(255, 255, 255, 0.12)",
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={handleBack}
          disabled={currentStep === 0}
          sx={{
            color: brandColor,
            width: 56,
            height: 56,
            "&:hover": {
              bgcolor: alpha(brandColor, 0.1),
            },
            "&:disabled": {
              color: perlColors.textSecondary,
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {/* Next Button - Disabled on last step */}
        <IconButton
          onClick={handleNext}
          disabled={nextButtonDisabled}
          sx={{
            color: brandColor,
            width: 56,
            height: 56,
            "&:hover": {
              bgcolor: alpha(brandColor, 0.1),
            },
            "&:disabled": {
              color: perlColors.textSecondary,
            },
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 32 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
